import AssessmentIcon from '@mui/icons-material/Assessment';
import BugReportIcon from '@mui/icons-material/BugReport';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import TimelineIcon from '@mui/icons-material/Timeline';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useRedirect } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { DocumentDetailResponse, DocumentType } from '../../../../interfaces/document';
import referralService from '../../../../services/referral';
import { PageHeader } from '../../../common/PageHeader';
import DetectedDocumentActionDialog from '../../DetectedDocumentActionDialog';
import FieldDisplay from '../../FieldDisplay';
import { COMMON_COLORS } from '../../constants';
import { useDetectedDocumentActions } from '../../hooks/useDetectedDocumentActions';
import { getConfidenceColorStyles, getStatusColorStyles } from '../../shared';
import { getDocumentsLibraryUrlWithFilters } from '../../utils/navigationHelpers';

interface ProcessingDetailsTabProps {
  document: DocumentDetailResponse;
  onActionComplete?: () => void;
}

export const ProcessingDetailsTab: React.FC<ProcessingDetailsTabProps> = ({
  document,
  onActionComplete,
}) => {
  const [showDebugData, setShowDebugData] = useState<Record<string, boolean>>({});
  const [documentTabs, setDocumentTabs] = useState<Record<string, number>>({});
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const redirect = useRedirect();
  const location = useLocation();

  const {
    dialogState,
    snackbar,
    openChangeType,
    openDiscard,
    openPatientDiscard,
    closeDialog,
    setReason,
    setCrmNote,
    setNewType,
    confirm,
    closeSnackbar,
  } = useDetectedDocumentActions(onActionComplete);

  useEffect(() => {
    let cancelled = false;
    referralService.fetchDocumentTypes().then((res) => {
      if (cancelled || !res) return;
      setDocumentTypes(res.documentContents || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!document) {
    return (
      <Box>
        <Typography variant='h6' sx={{ mb: 3 }}>
          Processing Details
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          No document data available
        </Typography>
      </Box>
    );
  }

  const formatDate = (dateString: string): string => {
    try {
      if (!dateString) return 'Unknown date';
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const toggleDebugData = (key: string) => {
    setShowDebugData((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTabChange = (docId: number, newValue: number) => {
    setDocumentTabs((prev) => ({ ...prev, [docId]: newValue }));
  };

  const renderExtractedDataSummary = (data: Record<string, any> | null) => {
    if (!data) return 'No data';
    const fieldCount = Object.keys(data).length;
    const nonNullFields = Object.values(data).filter((v) => v !== null && v !== '').length;
    return `${nonNullFields}/${fieldCount} fields`;
  };

  const getProcessingStepIcon = (step: string | null) => {
    switch (step) {
      case 'received':
        return <FolderIcon fontSize='small' />;
      case 'classification':
        return <SearchIcon fontSize='small' />;
      case 'extraction':
        return <DataObjectIcon fontSize='small' />;
      case 'validation':
        return <VerifiedIcon fontSize='small' />;
      case 'post-processing':
        return <PlayArrowIcon fontSize='small' />;
      case 'upload-to-s3':
        return <LinkIcon fontSize='small' />;
      default:
        return <InfoIcon fontSize='small' />;
    }
  };

  const getOrganizedProcessingData = () => {
    return {
      documentHistory: Array.isArray(document.processingHistory) ? document.processingHistory : [],
      documentAttempts: Array.isArray(document.processingAttempts)
        ? document.processingAttempts
        : [],
      detectedDocumentAttempts:
        document.detectedDocuments?.reduce((acc, dd) => {
          if (dd.documentType) {
            acc[dd.documentType] = Array.isArray(dd.processingAttempts)
              ? dd.processingAttempts
              : [];
          }
          return acc;
        }, {} as Record<string, any[]>) || {},
      patientAttempts:
        document.detectedDocuments?.flatMap(
          (dd) =>
            dd.patientDocuments?.map((pd) => ({
              patientId: pd.id,
              patientIdentifier: pd.patientIdentifier,
              documentType: dd.documentType,
              attempts: Array.isArray(pd.processingAttempts) ? pd.processingAttempts : [],
            })) || [],
        ) || [],
    };
  };

  const getDocumentTypeIcon = (documentType: string) => {
    if (documentType === 'referral-order') {
      return <DescriptionIcon fontSize='small' />;
    }
    if (documentType === 'prior-auth') {
      return <VerifiedIcon fontSize='small' />;
    }
    return <FolderIcon fontSize='small' />;
  };

  const getDocumentTypeColors = (documentType: string) => {
    if (documentType === 'referral-order') {
      return { bg: '#dbeafe', color: '#1e40af', borderColor: '#1e40af' };
    }
    if (documentType === 'prior-auth') {
      return { bg: '#ecfdf5', color: '#059669', borderColor: '#059669' };
    }
    return { bg: '#f8f9fa', color: '#4b5563', borderColor: '#6b7280' };
  };

  const canShowExtractedData = (detectedDoc: any) => {
    const enabledTypes = ['referral-order', 'prior-auth'];
    return (
      enabledTypes.includes(detectedDoc.documentType) && detectedDoc.patientDocuments?.length > 0
    );
  };

  const TabPanel = ({ children, value, index, ...other }: any) => {
    return (
      <div
        role='tabpanel'
        hidden={value !== index}
        id={`document-tabpanel-${index}`}
        aria-labelledby={`document-tab-${index}`}
        {...other}>
        {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
      </div>
    );
  };

  const renderExtractedData = (
    data: Record<string, any> | null,
    fieldConfidence?: Record<string, number>,
  ) => {
    if (!data) {
      return (
        <Typography variant='body2' color='text.secondary'>
          No data available
        </Typography>
      );
    }

    const { field_confidence, ...displayData } = data;
    const sortedEntries = Object.entries(displayData).sort(([keyA], [keyB]) => {
      const valueA = displayData[keyA];
      const valueB = displayData[keyB];
      if (!valueA && valueB) {
        return 1;
      }
      if (valueA && !valueB) {
        return -1;
      }
      return 0;
    });

    return (
      <Grid container spacing={1.5} sx={{ mt: 1 }}>
        {sortedEntries.map(([key, value]) => {
          const confidence = fieldConfidence?.[key];
          const hasValue = value !== null && value !== '' && value !== undefined;

          return (
            <FieldDisplay
              key={key}
              label={key.replace(/_/g, ' ')}
              value={value}
              gridSize={{ xs: 12, md: 4 }}
              variant='boxed'
              confidence={confidence ? Math.round(confidence * 100) : undefined}
              formatter={(val) =>
                hasValue
                  ? typeof val === 'object'
                    ? JSON.stringify(val, null, 2)
                    : String(val)
                  : '—'
              }
            />
          );
        })}
      </Grid>
    );
  };

  return (
    <Box>
      <PageHeader
        showBackButton={true}
        onBack={() => redirect(getDocumentsLibraryUrlWithFilters(location.search))}
        backButtonLabel='Back to Library'
        breadcrumbs={[
          {
            label: 'Documents',
            onClick: () => redirect(getDocumentsLibraryUrlWithFilters(location.search)),
          },
          {
            label: 'Library',
            onClick: () => redirect(getDocumentsLibraryUrlWithFilters(location.search)),
          },
          { label: `${document.externalId || document.documentId}`, active: true },
        ]}
      />

      <Typography variant='h6' sx={{ mb: 3 }}>
        Processing Details
      </Typography>

      {/* Document Overview */}
      <Box sx={{ mb: 4 }}>
        <Typography variant='h6' sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon /> Document Processing Overview
        </Typography>
        <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
          <Grid container spacing={3}>
            <FieldDisplay
              label='Total Retry Count'
              value={document.processingRetryCount || 1}
              gridSize={{ xs: 6, md: 3 }}
              variant='simple'
              fontWeight='semibold'
            />
            <FieldDisplay
              label='Last Attempt'
              value={
                document.lastProcessingAttempt ? formatDate(document.lastProcessingAttempt) : 'None'
              }
              gridSize={{ xs: 6, md: 3 }}
              variant='simple'
            />
            <FieldDisplay
              label='Current Step'
              value={document.currentProcessingStep}
              gridSize={{ xs: 6, md: 3 }}
              variant='simple'
            />
            <FieldDisplay
              label='Requires Review'
              value={document.requiresReview ? 'Yes' : 'No'}
              gridSize={{ xs: 6, md: 3 }}
              variant='simple'
              renderAsChip={true}
              chipColor={
                document.requiresReview ? COMMON_COLORS.WARNING_ORANGE : COMMON_COLORS.SUCCESS_GREEN
              }
            />
          </Grid>
        </Box>
      </Box>

      {/* Processing History - Clean Timeline */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: 'grey.50',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
            },
          }}>
          <TimelineIcon sx={{ mr: 1 }} />
          <Typography variant='h6'>Document Processing Timeline</Typography>
          <Box sx={{ ml: 2, display: 'flex', gap: 1 }}>
            {(() => {
              const organized = getOrganizedProcessingData();
              const lastEvent = organized.documentHistory[0];
              return (
                <Chip
                  label={lastEvent?.status || 'Unknown'}
                  size='small'
                  variant='filled'
                  sx={getStatusColorStyles(lastEvent?.status || '')}
                />
              );
            })()}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {(() => {
            const organized = getOrganizedProcessingData();

            // Use only processing history for main timeline - this is the high-level workflow
            const mainEvents = organized.documentHistory.map((event) => ({
              ...event,
              source: 'history',
              contextType: 'document',
            }));

            return mainEvents.length > 0 ? (
              <Box sx={{ position: 'relative' }}>
                {/* Timeline line */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 20,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    bgcolor: 'divider',
                    zIndex: 0,
                  }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {mainEvents.map((event, index) => (
                    <Box
                      key={`${event.source}-${event.id}`}
                      sx={{
                        position: 'relative',
                        ml: 5,
                        p: 2,
                        border: 1,
                        borderColor:
                          event.status === 'failed'
                            ? '#e74c3c'
                            : event.status === 'completed'
                            ? '#1eaf84'
                            : '#9e9e9e',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                      }}>
                      {/* Timeline dot */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -29,
                          top: 16,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor:
                            event.status === 'failed'
                              ? '#e74c3c'
                              : event.status === 'completed'
                              ? '#1eaf84'
                              : '#9e9e9e',
                          border: '2px solid white',
                          zIndex: 1,
                        }}
                      />

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant='body1'
                            fontWeight='600'
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {getProcessingStepIcon(event.step)}
                            {event.step
                              ? event.step
                                  .replace('-', ' ')
                                  .replace(/\b\w/g, (l: string) => l.toUpperCase())
                              : 'Unknown Step'}
                            {(() => {
                              const eventDetectedDocId =
                                event.processingMetadata?.detectedDocumentId;
                              if (eventDetectedDocId) {
                                const dd = document.detectedDocuments?.find(
                                  (d) => d.id === eventDetectedDocId,
                                );
                                const label = dd?.documentType
                                  ? `Detected: ${dd.documentType
                                      .replace(/-/g, ' ')
                                      .replace(/\b\w/g, (l) => l.toUpperCase())}`
                                  : `Detected #${eventDetectedDocId}`;
                                return (
                                  <Chip
                                    label={label}
                                    size='small'
                                    variant='outlined'
                                    sx={{
                                      fontSize: '0.65rem',
                                      height: 20,
                                      bgcolor: '#eef2ff',
                                      borderColor: '#6366f1',
                                      color: '#3730a3',
                                    }}
                                  />
                                );
                              }
                              return (
                                <Chip
                                  label='Document-level'
                                  size='small'
                                  variant='outlined'
                                  sx={{
                                    fontSize: '0.65rem',
                                    height: 20,
                                    bgcolor: '#f1f5f9',
                                    borderColor: '#94a3b8',
                                    color: '#475569',
                                  }}
                                />
                              );
                            })()}
                          </Typography>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ display: 'block', mb: 1 }}>
                            {formatDate(event.createdAt)} • Attempt {event.attemptNumber || 1}
                            {event.retryCount > 0 && ` • Retry ${event.retryCount}`}
                          </Typography>

                          {event.errorMessage && (
                            <Typography
                              variant='body2'
                              color='error.main'
                              sx={{
                                mt: 1,
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                              }}>
                              <ErrorIcon fontSize='small' /> {event.errorMessage}
                            </Typography>
                          )}

                          {event.processingMetadata?.message && (
                            <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{
                                mt: 1,
                                fontStyle: 'italic',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}>
                              <InfoIcon fontSize='small' /> {event.processingMetadata.message}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                            label={event.status || 'Unknown'}
                            size='small'
                            variant='filled'
                            sx={getStatusColorStyles(event.status || '')}
                          />

                          {event.processingMetadata &&
                            Object.keys(event.processingMetadata).length > 0 && (
                              <Button
                                size='small'
                                startIcon={<BugReportIcon />}
                                onClick={() => toggleDebugData(`${event.source}-${event.id}`)}
                                sx={{ textTransform: 'none', minWidth: 'auto' }}
                                variant='outlined'
                                color='inherit'>
                                See Details
                              </Button>
                            )}
                        </Box>
                      </Box>

                      {/* Technical metadata - expanded */}
                      {event.processingMetadata &&
                        Object.keys(event.processingMetadata).length > 0 && (
                          <Collapse in={showDebugData[`${event.source}-${event.id}`]}>
                            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                fontWeight='600'
                                sx={{ mb: 1, display: 'block' }}>
                                Technical Details:
                              </Typography>

                              <Box>
                                <Typography
                                  variant='caption'
                                  sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                  Processing Metadata:
                                </Typography>
                                <Typography
                                  variant='caption'
                                  fontFamily='monospace'
                                  sx={{
                                    p: 1.5,
                                    bgcolor: 'grey.100',
                                    borderRadius: 1,
                                    fontSize: '0.7rem',
                                    display: 'block',
                                    whiteSpace: 'pre-wrap',
                                    border: 1,
                                    borderColor: 'grey.300',
                                  }}>
                                  {JSON.stringify(event.processingMetadata, null, 2)}
                                </Typography>
                              </Box>
                            </Box>
                          </Collapse>
                        )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No processing history available
              </Typography>
            );
          })()}
        </AccordionDetails>
      </Accordion>

      {/* Detected Documents */}
      <Typography
        variant='subtitle1'
        fontWeight='600'
        sx={{ mb: 3, mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DescriptionIcon sx={{ fontSize: 20 }} /> Detected Documents (
        {document.detectedDocuments?.length || 0})
      </Typography>

      {document.detectedDocuments?.map((detectedDoc, docIndex) => {
        const colors = getDocumentTypeColors(detectedDoc.documentType);

        return (
          <Accordion
            key={detectedDoc.id}
            defaultExpanded={docIndex === 0 || detectedDoc.patientDocuments?.length > 0}
            sx={{ mb: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: colors.bg,
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                },
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {React.cloneElement(getDocumentTypeIcon(detectedDoc.documentType), {
                  sx: { fontSize: 18, color: colors.color },
                })}
                <Typography
                  variant='body1'
                  fontWeight='600'
                  sx={{
                    color: colors.color,
                  }}>
                  {detectedDoc.documentType
                    ?.replace('-', ' ')
                    .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Unknown Type'}
                </Typography>
              </Box>
              <Box sx={{ ml: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={
                    detectedDoc.pageStart === detectedDoc.pageEnd
                      ? `Page ${detectedDoc.pageStart}`
                      : `Pages ${detectedDoc.pageStart}-${detectedDoc.pageEnd}`
                  }
                  size='small'
                  variant='outlined'
                  sx={{ fontSize: '0.7rem' }}
                />
                <Chip
                  label={`${((detectedDoc.confidence || 0) * 100).toFixed(1)}%`}
                  size='small'
                  variant='filled'
                  sx={getConfidenceColorStyles(detectedDoc.confidence || 0)}
                />
                <Chip
                  icon={<GroupIcon sx={{ fontSize: 14 }} />}
                  label={
                    (detectedDoc.patientDocuments?.length ?? 0) === 0
                      ? 'No patients'
                      : (detectedDoc.patientDocuments?.length ?? 0) === 1
                      ? '1 patient'
                      : `${detectedDoc.patientDocuments?.length} patients`
                  }
                  size='small'
                  variant='outlined'
                  sx={
                    detectedDoc.patientDocuments?.length > 0
                      ? { bgcolor: '#1eaf84', color: '#ffffff', borderColor: '#1eaf84' }
                      : { bgcolor: '#9e9e9e', color: '#ffffff', borderColor: '#9e9e9e' }
                  }
                />
                <Tooltip title='Change document type'>
                  <IconButton
                    size='small'
                    aria-label='change document type'
                    onClick={(e) => {
                      e.stopPropagation();
                      openChangeType({
                        documentId: document.documentId,
                        detectedDocumentId: detectedDoc.id,
                        currentType: detectedDoc.documentType,
                      });
                    }}>
                    <EditIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Discard detected document'>
                  <IconButton
                    size='small'
                    aria-label='discard detected document'
                    onClick={(e) => {
                      e.stopPropagation();
                      openDiscard({
                        documentId: document.documentId,
                        detectedDocumentId: detectedDoc.id,
                        currentType: detectedDoc.documentType,
                      });
                    }}>
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={documentTabs[detectedDoc.id] ?? 0}
                  onChange={(_, newValue) => handleTabChange(detectedDoc.id, newValue)}
                  aria-label={`Document ${detectedDoc.documentType} tabs`}
                  sx={{ px: 2 }}>
                  <Tab
                    label='Classification'
                    icon={<SearchIcon fontSize='small' />}
                    iconPosition='start'
                    sx={{ textTransform: 'none', minHeight: 48 }}
                  />
                  {detectedDoc.extractedData && (
                    <Tab
                      label='Extraction'
                      icon={<DataObjectIcon fontSize='small' />}
                      iconPosition='start'
                      sx={{ textTransform: 'none', minHeight: 48 }}
                    />
                  )}
                  {detectedDoc.patientDocuments && detectedDoc.patientDocuments.length > 0 && (
                    <Tab
                      label={`Patients (${detectedDoc.patientDocuments.length})`}
                      icon={<GroupIcon fontSize='small' />}
                      iconPosition='start'
                      sx={{ textTransform: 'none', minHeight: 48 }}
                    />
                  )}
                </Tabs>
              </Box>

              {/* Classification Tab */}
              <TabPanel value={documentTabs[detectedDoc.id] ?? 0} index={0}>
                <Box sx={{ px: 2 }}>
                  {/* Processing attempts for this document type */}
                  {detectedDoc.processingAttempts &&
                    Array.isArray(detectedDoc.processingAttempts) &&
                    detectedDoc.processingAttempts.length > 0 && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          fontWeight='600'
                          sx={{ mb: 1, display: 'block' }}>
                          Processing Attempts for this Document Type:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {detectedDoc.processingAttempts.map((attempt, idx) => (
                            <Chip
                              key={idx}
                              label={`${attempt.step || 'unknown'}: ${attempt.status || 'unknown'}${
                                attempt.confidence
                                  ? ` (${(attempt.confidence * 100).toFixed(1)}%)`
                                  : ''
                              }`}
                              size='small'
                              variant='outlined'
                              sx={{
                                fontSize: '0.7rem',
                                ...getStatusColorStyles(attempt.status || ''),
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                  <Grid container spacing={2}>
                    <FieldDisplay
                      label='Detection Confidence'
                      value={`${((detectedDoc.confidence || 0) * 100).toFixed(1)}%`}
                      gridSize={{ xs: 6, md: 4 }}
                      variant='simple'
                      fontWeight='semibold'
                    />
                    <FieldDisplay
                      label='Page Range'
                      value={
                        detectedDoc.pageStart === detectedDoc.pageEnd
                          ? `${detectedDoc.pageStart}`
                          : `${detectedDoc.pageStart}-${detectedDoc.pageEnd}`
                      }
                      gridSize={{ xs: 6, md: 4 }}
                      variant='simple'
                    />
                    <FieldDisplay
                      label='Detected Clinic'
                      value={detectedDoc.clinicId ? `Clinic ${detectedDoc.clinicId}` : 'None'}
                      gridSize={{ xs: 6, md: 4 }}
                      variant='simple'
                    />
                    <FieldDisplay
                      label='Processing Status'
                      value={canShowExtractedData(detectedDoc) ? 'Enabled' : 'Disabled'}
                      gridSize={{ xs: 6, md: 4 }}
                      variant='simple'
                      renderAsChip={true}
                      chipColor={
                        canShowExtractedData(detectedDoc)
                          ? COMMON_COLORS.SUCCESS_GREEN
                          : COMMON_COLORS.NEUTRAL_GRAY
                      }
                    />
                  </Grid>
                </Box>
              </TabPanel>

              {/* Extraction Tab */}
              {detectedDoc.extractedData && (
                <TabPanel value={documentTabs[detectedDoc.id] ?? 0} index={1}>
                  <Box sx={{ px: 2 }}>
                    <Typography variant='body2' fontWeight='600' sx={{ mb: 2 }}>
                      Document Classification Data
                    </Typography>

                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {renderExtractedData(
                        detectedDoc.extractedData,
                        detectedDoc.extractedData?.field_confidence,
                      )}
                    </Box>
                  </Box>
                </TabPanel>
              )}

              {/* Patients Tab */}
              {detectedDoc.patientDocuments && detectedDoc.patientDocuments.length > 0 && (
                <TabPanel
                  value={documentTabs[detectedDoc.id] ?? 0}
                  index={detectedDoc.extractedData ? 2 : 1}>
                  <Box sx={{ px: 2 }}>
                    <Typography
                      variant='body1'
                      fontWeight='600'
                      sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon sx={{ fontSize: 18 }} /> Detected Patients (
                      {detectedDoc.patientDocuments.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {detectedDoc.patientDocuments.map((patientDoc, index) => (
                        <Box key={patientDoc.id}>
                          <Box
                            sx={{
                              p: 2,
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: 'grey.50',
                              pb: 6,
                            }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                              }}>
                              <Typography
                                variant='body2'
                                fontWeight='600'
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon sx={{ fontSize: 16 }} />
                                {patientDoc.patientIdentifier || 'Unknown Patient'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={patientDoc.extractionStatus}
                                  size='small'
                                  sx={getStatusColorStyles(patientDoc.extractionStatus)}
                                />
                                <Tooltip title='Discard this patient'>
                                  <IconButton
                                    size='small'
                                    aria-label='discard patient'
                                    onClick={() =>
                                      openPatientDiscard({
                                        documentId: document.documentId,
                                        detectedDocumentId: detectedDoc.id,
                                        patientDocumentId: patientDoc.id,
                                        patientIdentifier:
                                          patientDoc.patientIdentifier || 'Unknown Patient',
                                      })
                                    }>
                                    <DeleteOutlineIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 1 }}>
                              <FieldDisplay
                                label='Doc ID'
                                value={patientDoc.clinicInternalDocumentId || '—'}
                                gridSize={{ xs: 6, md: 6 }}
                                variant='simple'
                              />
                              <FieldDisplay
                                label='Extracted'
                                value={
                                  patientDoc.extractionDate
                                    ? formatDate(patientDoc.extractionDate)
                                    : '—'
                                }
                                gridSize={{ xs: 6, md: 6 }}
                                variant='simple'
                              />
                            </Grid>

                            {/* Processing attempts for this patient */}
                            {patientDoc.processingAttempts &&
                              Array.isArray(patientDoc.processingAttempts) &&
                              patientDoc.processingAttempts.length > 0 && (
                                <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    fontWeight='600'
                                    sx={{ mb: 0.5, display: 'block' }}>
                                    Patient Processing Attempts:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {patientDoc.processingAttempts.map((attempt, idx) => (
                                      <Chip
                                        key={idx}
                                        label={`${attempt.step || 'unknown'}: ${
                                          attempt.status || 'unknown'
                                        }`}
                                        size='small'
                                        variant='filled'
                                        sx={{
                                          fontSize: '0.65rem',
                                          height: 16,
                                          ...getStatusColorStyles(attempt.status || ''),
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}

                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AssessmentIcon fontSize='small' />
                                {renderExtractedDataSummary(patientDoc.extractedData)}
                              </Typography>
                              <Button
                                size='small'
                                startIcon={<DataObjectIcon />}
                                onClick={() => toggleDebugData(`patient-${patientDoc.id}`)}
                                sx={{ textTransform: 'none' }}
                                variant='outlined'
                                color='primary'>
                                {showDebugData[`patient-${patientDoc.id}`]
                                  ? 'Hide'
                                  : 'Extracted Data'}
                              </Button>
                            </Box>

                            {/* Debug Data */}
                            <Collapse in={showDebugData[`patient-${patientDoc.id}`]}>
                              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                <Typography
                                  variant='body2'
                                  fontWeight='600'
                                  sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <DataObjectIcon sx={{ fontSize: 16 }} />
                                  Raw LLM Output
                                </Typography>
                                {renderExtractedData(
                                  patientDoc.extractedData,
                                  patientDoc.extractedData?.field_confidence,
                                )}

                                {patientDoc.crmMetadata && (
                                  <Box sx={{ mt: 5 }}>
                                    <Typography
                                      variant='body2'
                                      fontWeight='600'
                                      sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LinkIcon sx={{ fontSize: 16 }} />
                                      CRM Metadata
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      {Object.entries(patientDoc.crmMetadata).map(
                                        ([key, value]) => (
                                          <Box
                                            key={key}
                                            sx={{
                                              p: 1.5,
                                              border: 1,
                                              borderColor: 'divider',
                                              borderRadius: 1,
                                              bgcolor: 'background.paper',
                                              minWidth: 200,
                                              flex: '1 1 auto',
                                            }}>
                                            <Typography variant='caption' color='text.secondary'>
                                              {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                                            </Typography>
                                            <Typography
                                              variant='body2'
                                              sx={{
                                                wordBreak: 'break-word',
                                                fontSize: '0.875rem',
                                                fontFamily: key === 'id' ? 'monospace' : 'inherit',
                                              }}>
                                              {value ? String(value) : '—'}
                                            </Typography>
                                          </Box>
                                        ),
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </Box>
                          {index < detectedDoc.patientDocuments!.length - 1 && (
                            <Divider sx={{ my: 1 }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </TabPanel>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {(!document.detectedDocuments || document.detectedDocuments.length === 0) && (
        <Typography variant='body2' color='text.secondary'>
          No detected documents available
        </Typography>
      )}

      <DetectedDocumentActionDialog
        open={dialogState.open}
        mode={dialogState.mode}
        currentType={dialogState.currentType}
        patientIdentifier={dialogState.patientIdentifier}
        newType={dialogState.newType}
        reason={dialogState.reason}
        crmNote={dialogState.crmNote}
        loading={dialogState.loading}
        documentTypes={documentTypes}
        onClose={closeDialog}
        onReasonChange={setReason}
        onCrmNoteChange={setCrmNote}
        onNewTypeChange={setNewType}
        onConfirm={confirm}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
